import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./driver-theme.css";
import { useAuth } from "../../hooks/useAuth";
import { USER_TYPES } from "../../constants/userTypes";
import { hasCompletedOnboarding, completeOnboarding } from "./onboardingStorage";

// Dispatched by the Navbar "Take the tour" dropdown item to replay the tour.
export const START_TOUR_EVENT = "sulhafa:start-tour";

// How long an anchored step waits for its target element to appear in the DOM
// after a cross-page navigation before giving up (popover then shows centered).
const ELEMENT_WAIT_MS = 8000;

const waitForElement = (selector, timeout = 5000) =>
  new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeout);
  });

// Some anchors exist twice (e.g. desktop vs medium-screen navbar); pick the visible one.
const visibleTourElement = (selector) => {
  const els = Array.from(document.querySelectorAll(selector));
  return els.find((el) => el.offsetParent !== null) ?? els[0] ?? null;
};

// Mirrors the user-type color scheme used in Navbar.getColorScheme()
const USER_TYPE_COLORS = {
  PET_GUARDIAN: { primary: "#8548A8", hover: "#7A4397" },
  VETERINARIAN: { primary: "#00D4B3", hover: "#00C4A7" },
  SERVICE_PROVIDER: { primary: "#D65745", hover: "#C7493A" },
  FIRM: { primary: "#1B3A75", hover: "#162E5E" },
};

const applyTourTheme = (userType) => {
  const colors = USER_TYPE_COLORS[userType] ?? USER_TYPE_COLORS.PET_GUARDIAN;
  document.documentElement.style.setProperty("--tour-primary", colors.primary);
  document.documentElement.style.setProperty("--tour-primary-hover", colors.hover);
};

const OnboardingTour = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user, getUserType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef(null);
  const autoStartedRef = useRef(false);

  const userId = user?.id ?? null;

  const startTour = useCallback(async () => {
    if (driverRef.current || !userId) return;

    // The tour starts on the feed so the "first post" step can anchor.
    if (
      window.location.pathname !== "/social" &&
      window.location.pathname !== "/social/feed"
    ) {
      navigate("/social");
      await waitForElement('[data-tour="create-post"]');
    }

    // Navigate and jump to a step. Anchored steps carry their own
    // `waitForElement`, so driver.js polls the DOM and activates the step
    // as soon as the new page's element appears — no manual waiting here.
    const goTo = (driverObj, index, path) => {
      if (path) navigate(path);
      driverObj.moveTo(index);
    };

    // Per-user-type steps: only surface pages this user can actually open.
    const userType = getUserType();
    applyTourTheme(userType);
    const hasMedicalStep =
      userType === USER_TYPES.PET_GUARDIAN ||
      userType === USER_TYPES.SERVICE_PROVIDER;
    const hasClinicStep = userType === USER_TYPES.VETERINARIAN;
    const medicalPath =
      userType === USER_TYPES.SERVICE_PROVIDER
        ? "/medical/dashboard/petshopowner"
        : "/medical/dashboard/petguardian";

    let driverObj;
    const steps = [];
    const idx = {};
    const push = (key, step) => {
      idx[key] = steps.length;
      steps.push(step);
    };
    // Wire a step's Next/Previous to jump to another step, navigating first
    // when the target step lives on a different page.
    const wireNav = (key, { next, prev }) => {
      const popover = steps[idx[key]].popover;
      if (next) {
        popover.onNextClick = () => goTo(driverObj, idx[next.to], next.path);
      }
      if (prev) {
        popover.onPrevClick = () => goTo(driverObj, idx[prev.to], prev.path);
      }
    };

    push("welcome", {
      popover: {
        title: t("onboarding.welcome.title"),
        description: t("onboarding.welcome.description"),
        align: "center",
      },
    });
    push("post", {
      element: '[data-tour="create-post"]',
      waitForElement: ELEMENT_WAIT_MS,
      popover: {
        title: t("onboarding.post.title"),
        description: t("onboarding.post.description"),
        side: "bottom",
      },
    });
    push("community", {
      element: visibleTourElement('[data-tour="community-nav"]'),
      popover: {
        title: t("onboarding.community.title"),
        description: t("onboarding.community.description"),
        side: "bottom",
      },
    });
    push("communities", {
      popover: {
        title: t("onboarding.communityPage.title"),
        description: t("onboarding.communityPage.description"),
        align: "center",
      },
    });
    push("addPet", {
      element: '[data-tour="add-pet"]',
      waitForElement: ELEMENT_WAIT_MS,
      popover: {
        title: t("onboarding.addPet.title"),
        description: t("onboarding.addPet.description"),
        side: "bottom",
      },
    });
    if (hasMedicalStep) {
      // Centered modal — the dashboard anchor was unreliable under slow loads.
      push("medical", {
        popover: {
          title: t(
            userType === USER_TYPES.SERVICE_PROVIDER
              ? "onboarding.medicalService.title"
              : "onboarding.medicalGuardian.title"
          ),
          description: t(
            userType === USER_TYPES.SERVICE_PROVIDER
              ? "onboarding.medicalService.description"
              : "onboarding.medicalGuardian.description"
          ),
          align: "center",
        },
      });
    }
    // Adoption is reachable by every user type that sees the tour.
    push("adoption", {
      element: '[data-tour="adoption-page"]',
      waitForElement: ELEMENT_WAIT_MS,
      popover: {
        title: t("onboarding.adoption.title"),
        description: t("onboarding.adoption.description"),
        side: "top",
      },
    });
    if (hasClinicStep) {
      push("clinic", {
        element: visibleTourElement('[data-tour="medical-nav"]'),
        popover: {
          title: t("onboarding.clinic.title"),
          description: t("onboarding.clinic.description"),
          side: "bottom",
        },
      });
    }
    push("privacy", {
      element: '[data-tour="privacy-settings"]',
      waitForElement: ELEMENT_WAIT_MS,
      popover: {
        title: t("onboarding.privacy.title"),
        description: t("onboarding.privacy.description"),
        side: "top",
      },
    });
    push("done", {
      popover: {
        title: t("onboarding.done.title"),
        description: t("onboarding.done.description"),
        align: "center",
      },
    });

    // Forward navigation
    wireNav("community", {
      next: { to: "communities", path: "/social/communities" },
    });
    wireNav("communities", {
      next: { to: "addPet", path: "/dashboard/pets" },
      prev: { to: "community", path: "/social" },
    });
    if (hasMedicalStep) {
      wireNav("addPet", {
        next: { to: "medical", path: medicalPath },
        prev: { to: "communities", path: "/social/communities" },
      });
      wireNav("medical", {
        next: { to: "adoption", path: "/adoption" },
        prev: { to: "addPet", path: "/dashboard/pets" },
      });
      wireNav("adoption", {
        next: hasClinicStep
          ? { to: "clinic" } // clinic anchors the persistent navbar — no navigation
          : { to: "privacy", path: "/account/preferences/privacy" },
        prev: { to: "medical", path: medicalPath },
      });
    } else {
      // Veterinarians and firms go straight from Add Pet to Adoption
      wireNav("addPet", {
        next: { to: "adoption", path: "/adoption" },
        prev: { to: "communities", path: "/social/communities" },
      });
      wireNav("adoption", {
        next: hasClinicStep
          ? { to: "clinic" }
          : { to: "privacy", path: "/account/preferences/privacy" },
        prev: { to: "addPet", path: "/dashboard/pets" },
      });
    }
    if (hasClinicStep) {
      wireNav("clinic", {
        next: { to: "privacy", path: "/account/preferences/privacy" },
        // The user may have arrived here from Privacy, so go back explicitly
        prev: { to: "adoption", path: "/adoption" },
      });
    }
    // Everyone else's Previous from Privacy goes back to Adoption
    wireNav("privacy", {
      prev: hasClinicStep
        ? { to: "clinic" } // clinic anchors the navbar, still visible here
        : { to: "adoption", path: "/adoption" },
    });

    driverObj = driver({
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 12,
      smoothScroll: true,
      nextBtnText: t("onboarding.buttons.next"),
      prevBtnText: t("onboarding.buttons.prev"),
      doneBtnText: t("onboarding.buttons.done"),
      progressText: t("onboarding.buttons.progress"),
      onDestroyed: () => {
        // Fired on finish, skip, and close — the tour never auto-shows again.
        driverRef.current = null;
        completeOnboarding(userId);
      },
      steps,
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [navigate, userId, getUserType, t]);

  // Auto-start for first-time users: the per-device completion cookie is
  // the "already shown" guard, and the backend is_first_login flag decides
  // whether auto-showing is appropriate at all (veterans are never nagged,
  // even on a new device with no cookie).
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    if (autoStartedRef.current) return;
    if (
      location.pathname !== "/social" &&
      location.pathname !== "/social/feed"
    )
      return;

    // Completed (or dismissed) on this device already — never re-show,
    // even while the backend flag stays true during a long first session.
    if (hasCompletedOnboarding(userId)) return;

    // Only auto-start when the backend confirms this is a first-time user
    // (is_first_login === true). Veterans on a new device are not nagged.
    if (user?.is_first_login !== true) return;

    autoStartedRef.current = true;
    const timer = setTimeout(startTour, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, userId, user, location.pathname, startTour]);

  // Replay on demand from the profile dropdown.
  useEffect(() => {
    const handler = () => {
      startTour();
    };
    window.addEventListener(START_TOUR_EVENT, handler);
    return () => window.removeEventListener(START_TOUR_EVENT, handler);
  }, [startTour]);

  return null;
};

export default OnboardingTour;
